/**
 * Saved-view spec helpers (§4). The `spec` field on a saved query is FE-owned,
 * opaque-to-the-server JSON with a versioned shape `{v:1, target, params}`.
 * These helpers read it back defensively (the store is loosely typed
 * `Record<string, unknown>`) so the Saved Views screen can label a row by its
 * target screen and route to it when opened.
 */
import type { MessageKey } from "@i18n/messages";
import type { SavedQueryOut, SavedQuerySpec, SavedQueryTarget } from "@api/types";

/** Router path for each saved-view target. Opening a view navigates here. */
const TARGET_ROUTE: Record<SavedQueryTarget, string> = {
  inbox: "/inbox",
  "cost-afe": "/cost-afe",
  "time-npt": "/time-npt",
  surveys: "/surveys",
  bha: "/bha",
  tubulars: "/tubulars",
  mud: "/mud",
  hse: "/hse",
};

/** i18n label key for each target — the "Opens in" badge text. */
const TARGET_LABEL: Record<SavedQueryTarget, MessageKey> = {
  inbox: "saved.target.inbox",
  "cost-afe": "saved.target.cost-afe",
  "time-npt": "saved.target.time-npt",
  surveys: "saved.target.surveys",
  bha: "saved.target.bha",
  tubulars: "saved.target.tubulars",
  mud: "saved.target.mud",
  hse: "saved.target.hse",
};

const TARGETS = Object.keys(TARGET_ROUTE) as SavedQueryTarget[];

function isTarget(v: unknown): v is SavedQueryTarget {
  return typeof v === "string" && (TARGETS as string[]).includes(v);
}

/** Read a saved query's spec into the typed shape, or null if it isn't v1. */
export function readSpec(q: SavedQueryOut): SavedQuerySpec | null {
  const spec = q.spec as Record<string, unknown> | null | undefined;
  if (!spec || spec.v !== 1 || !isTarget(spec.target)) return null;
  const params =
    spec.params && typeof spec.params === "object"
      ? (spec.params as Record<string, unknown>)
      : {};
  return { v: 1, target: spec.target, params };
}

/** The target of a saved query, or null when the spec is missing/unversioned. */
export function specTarget(q: SavedQueryOut): SavedQueryTarget | null {
  return readSpec(q)?.target ?? null;
}

/** i18n label key for a target, for the "Opens in" badge. */
export function targetLabelKey(target: SavedQueryTarget): MessageKey {
  return TARGET_LABEL[target];
}

/** Router path for a target — where "Open" navigates to. */
export function targetRoute(target: SavedQueryTarget): string {
  return TARGET_ROUTE[target];
}
